import React, { useState, useEffect, forwardRef, useImperativeHandle, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import POList from './mappings-po-list';
import PartsList from './mappings-parts-list';
import ReleaseList from './mappings-release-list';
import MappingsCreateReceiving from './mappings-create-receiving';
import Select from 'react-select';
import { Modal, Button } from 'react-bootstrap';
import { fetchPOsByVendorIds, editPOData, fetchItemReleases } from '../../feature-module/pomasters/store/slice';
import { fetchVendorsWithFilters } from '../../feature-module/vendormasters/store/slice';
import { FaCloudUploadAlt } from 'react-icons/fa';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { POService } from '../../service/api/po-service';
import { ReceivingShipmentService } from '../../service/api/receiving-shipment-service';
import { ShipmentService } from '../../service/api/shipment-service';
import { FeatureFlagService } from '../../service/api/feature-flag-service';
import { PrintLabelService } from '../../service/api/print-label-service';
import MappingsPrintPickTicketModal from './mappings-print-pick-ticket-modal';
import MappingsPrintModal from './mappings-print';
import MappingsOSDReportModal from './mappings-osd-report-modal';

import MappingsTile from './mappings-tile';
import MappingsTable from './mappings-table';
import MappingsSearch from './mappings-search';
import MappingsFilter from './mappings-filter';
import { selectReceivingFilterVisible } from './store/selectors';
import LoadingSpinner from '../../InitialPage/Sidebar/LoadingSpinner';
import MappingsReport from './mappings-report';
import { DocumentViewerModal } from '../../core/components';
import { showToast } from 'Core/redux/toast/slice';
import { saveAs } from 'Core/utils';
import { isAsnOriginShipment } from './mappings-asn';
import './mappings-content.css';

const customSelectStyles = {
  control: (provided) => ({
    ...provided,
    minHeight: '48px',
    borderRadius: '8px',
    borderColor: '#d9d9d9',
    boxShadow: 'none',
  }),
  multiValue: (provided) => ({
    ...provided,
    backgroundColor: '#e6f7ff',
    borderRadius: '6px',
    marginRight: 4,
    maxWidth: '100%',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  }),
  multiValueLabel: (provided) => ({
    ...provided,
    color: '#1890ff',
    fontWeight: 500,
    fontSize: 14,
    padding: '2px 6px',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    maxWidth: 120,
  }),
  multiValueRemove: (provided) => ({
    ...provided,
    color: '#1890ff',
    ':hover': {
      backgroundColor: '#bae7ff',
      color: '#0050b3',
    },
  }),
  valueContainer: (provided) => ({
    ...provided,
    flexWrap: 'wrap',
    maxHeight: 60,
    overflowY: 'auto',
  }),
};

const sameReceivingId = (a, b) => a != null && b != null && String(a) === String(b);

const firstAsnPartForPo = (parts, poId) => {
  const onPo = (parts || []).filter(part => sameReceivingId(part.poReceivingID, poId));
  return onPo.find(part => Number(part.receivedInThisShipment) > 0) || onPo[0] || null;
};

const normalizeOneDriveDoc = (response, fallbackFileName = 'document.pdf') => {
  const payload = response?.response;
  if (!payload) {
    return { exists: false, fileName: fallbackFileName, fileBase64: null, webUrl: null };
  }
  return {
    exists: Boolean(payload.exists ?? payload.Exists),
    fileName: payload.fileName || payload.FileName || fallbackFileName,
    fileBase64: payload.fileBase64 || payload.FileBase64 || null,
    webUrl: payload.webUrl || payload.WebUrl || null,
  };
};

const getBlobContentType = (fileName = '') => {
  const ext = String(fileName).split('.').pop()?.toLowerCase();
  if (ext === 'pdf') return 'application/pdf';
  if (ext === 'png') return 'image/png';
  if (ext === 'jpg' || ext === 'jpeg') return 'image/jpeg';
  if (ext === 'gif') return 'image/gif';
  if (ext === 'xlsx') return 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
  if (ext === 'xls') return 'application/vnd.ms-excel';
  if (ext === 'docx') return 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
  if (ext === 'doc') return 'application/msword';
  return 'application/pdf';
};

const base64ToBlob = (base64, fileName = '') => {
  const binary = atob(String(base64 || '').replace(/\s/g, ''));
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) {
    bytes[i] = binary.charCodeAt(i);
  }
  return new Blob([bytes], { type: getBlobContentType(fileName) });
};

const getReleaseReceiveState = (release) => {
  if (!release) {
    return {
      requestedQty: 0,
      receivedQty: 0,
      receivedInThisShipment: 0,
      isFullyReceived: false,
      isReceivedInThisShipment: false,
      canMarkAsReceived: false,
      remainingQty: 0,
    };
  }

  const requestedQty = Number(release.requestedQuantity) || 0;
  const receivedQty = Number(release.receivedQuantity) || 0;
  const receivedInThisShipment = Number(release.receivedInThisShipment) || 0;
  const isFullyReceived = requestedQty > 0 && receivedQty >= requestedQty;
  const isReceivedInThisShipment = receivedInThisShipment > 0;

  return {
    requestedQty,
    receivedQty,
    receivedInThisShipment,
    isFullyReceived,
    isReceivedInThisShipment,
    canMarkAsReceived: !release.isVoided && !isFullyReceived && !isReceivedInThisShipment,
    remainingQty: Math.max(requestedQty - receivedQty, 0),
  };
};

const MappingsContent = forwardRef(({ tileView, shipments, shipmentsLoading, onViewShipment, showMappingsUI, setShowMappingsUI, onToggleView, collapsed, onCollapse, onRefreshShipments, createdShipment: propCreatedShipment, createdShipmentId: propCreatedShipmentId, showReport: propShowReport, setShowReport: propSetShowReport }, ref) => {
  const dispatch = useDispatch();
  const filterVisible = useSelector(selectReceivingFilterVisible);
  const poState = useSelector(state => state.po);
  const vendors = useSelector(state => state.vendors.data) || [];
  const vendorsLoading = useSelector(state => state.vendors.loading);
  const itemReleases = poState.itemReleases || [];
  const itemReleasesLoading = poState.itemReleasesLoading;
  const [selectedVendorIds, setSelectedVendorIds] = useState([]);
  const [shipmentNumberVendorName, setShipmentNumberVendorName] = useState('');
  const [selectedPOId, setSelectedPOId] = useState(null);
  const [selectedPartId, setSelectedPartId] = useState(null);
  const [selectedRelease, setSelectedRelease] = useState(null);
  const [showReleaseModal, setShowReleaseModal] = useState(false);
  const [receivingQuantity, setReceivingQuantity] = useState('');
  const [isReleaseAlreadyReceived, setIsReleaseAlreadyReceived] = useState(false);
  const [isReleaseVoided, setIsReleaseVoided] = useState(false);
  const [isPartVoided, setIsPartVoided] = useState(false);
  const [selectedPOIds, setSelectedPOIds] = useState([]);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [uploadPOId, setUploadPOId] = useState(null);
  const [uploadFile, setUploadFile] = useState(null);
  const [poSearch, setPOSearch] = useState('');
  const [partsSearch, setPartsSearch] = useState('');
  const [multiplePOItems, setMultiplePOItems] = useState([]);
  const [multiplePOItemsLoading, setMultiplePOItemsLoading] = useState(false);
  const [shipmentLoading, setShipmentLoading] = useState(false);
  const [createdShipmentId, setCreatedShipmentId] = useState(propCreatedShipmentId || null);
  const [showReceiveModal, setShowReceiveModal] = useState(false);
  const [selectedItemForReceiving, setSelectedItemForReceiving] = useState(null);
  const [createdShipment, setCreatedShipment] = useState(propCreatedShipment || null);
  const [hasShipmentDoc, setHasShipmentDoc] = useState(false);
  const [hasPODoc, setHasPODoc] = useState(false);
  // Use props for showReport state
  const showReport = propShowReport || false;
  const setShowReport = propSetShowReport || (() => {});
  const [showPrintPickTicketModal, setShowPrintPickTicketModal] = useState(false);
  const [showOsdModal, setShowOsdModal] = useState(false);
  const [showPrintModal, setShowPrintModal] = useState(false);
  const [printModalPart, setPrintModalPart] = useState(null);
  const [releasesSearch, setReleasesSearch] = useState('');
  const [isCloseShipmentEnabled, setIsCloseShipmentEnabled] = useState(false);
  const [lastUpdateTime, setLastUpdateTime] = useState(Date.now());
  const [isEditingShipmentInfo, setIsEditingShipmentInfo] = useState(false);
  const [editShipmentComments, setEditShipmentComments] = useState('');
  const [editShipmentDate, setEditShipmentDate] = useState(null);
  const [updateShipmentLoading, setUpdateShipmentLoading] = useState(false);
  const [showDocumentViewer, setShowDocumentViewer] = useState(false);
  const [documentViewerUrl, setDocumentViewerUrl] = useState('');
  const [documentViewerName, setDocumentViewerName] = useState('');
  const [shipmentDoc, setShipmentDoc] = useState(null);
  const [shipmentDocLoading, setShipmentDocLoading] = useState(false);
  const [poDocs, setPoDocs] = useState({});
  const asnHydratedShipmentIdRef = useRef(null);

  const revokeDocumentViewerUrl = () => {
    if (documentViewerUrl?.startsWith('blob:')) {
      URL.revokeObjectURL(documentViewerUrl);
    }
  };

  const getUploadedDocumentInfo = (documentPath, fallbackFileName = '') => {
    if (!documentPath) {
      return { fileUrl: '', fileName: fallbackFileName || 'Document' };
    }

    const encodedFileName = documentPath.split('/').pop() || '';
    const decoded = decodeURIComponent(encodedFileName);
    const lastUnderscore = decoded.lastIndexOf('_');
    const extensionIndex = decoded.lastIndexOf('.');
    let fileName = fallbackFileName;

    if (!fileName) {
      if (lastUnderscore !== -1 && extensionIndex !== -1 && lastUnderscore < extensionIndex) {
        fileName = decoded.slice(0, lastUnderscore) + decoded.slice(extensionIndex);
      } else {
        fileName = decoded || 'Document';
      }
    }

    return { fileUrl: documentPath, fileName };
  };

  const openDocumentViewer = (documentPath, fallbackFileName = '') => {
    if (!documentPath) {
      dispatch(showToast({ message: 'Uploaded document is not available.', type: 'warning' }));
      return;
    }

    const { fileUrl, fileName } = getUploadedDocumentInfo(documentPath, fallbackFileName);

    revokeDocumentViewerUrl();
    setDocumentViewerUrl(fileUrl);
    setDocumentViewerName(fileName);
    setShowDocumentViewer(true);
  };

  const openOneDriveDocInViewer = (doc) => {
    if (!doc?.exists) {
      dispatch(showToast({ message: 'Document is not available.', type: 'warning' }));
      return;
    }

    revokeDocumentViewerUrl();

    let url = '';
    if (doc.fileBase64) {
      const blob = base64ToBlob(doc.fileBase64, doc.fileName);
      url = URL.createObjectURL(blob);
    } else if (doc.webUrl) {
      url = doc.webUrl;
    }

    if (!url) {
      dispatch(showToast({ message: 'Document is not available.', type: 'warning' }));
      return;
    }

    setDocumentViewerUrl(url);
    setDocumentViewerName(doc.fileName || 'Document');
    setShowDocumentViewer(true);
  };

  const fetchReceivingDoc = async (poNumber, includeContent = true) => {
    const shipmentNumber = createdShipment?.shipmentNumber;
    const fallbackName = poNumber
      ? `${shipmentNumber}_${poNumber}.pdf`
      : `${shipmentNumber || 'document'}.pdf`;
    const response = await ReceivingShipmentService.getShipDoc(shipmentNumber, poNumber, includeContent);
    return normalizeOneDriveDoc(response, fallbackName);
  };

  const handleViewUploadedDocument = async () => {
    try {
      let doc = shipmentDoc;
      if (!doc?.fileBase64) {
        setShipmentDocLoading(true);
        doc = await fetchReceivingDoc();
        setShipmentDoc(doc);
        setHasShipmentDoc(Boolean(doc?.exists));
      }
      if (!doc?.exists) {
        dispatch(showToast({ message: 'Shipment document not found in OneDrive.', type: 'warning' }));
        return;
      }
      openOneDriveDocInViewer(doc);
    } catch (error) {
      dispatch(showToast({ message: error.message || 'Failed to load shipment document.', type: 'danger' }));
    } finally {
      setShipmentDocLoading(false);
    }
  };

  const handleViewPODocument = async (po) => {
    const poNumber = po?.poNumber;
    if (!poNumber) {
      dispatch(showToast({ message: 'Could not determine the PO number for this document.', type: 'info' }));
      return;
    }

    try {
      let doc = poDocs[poNumber];
      if (!doc?.fileBase64) {
        doc = await fetchReceivingDoc(poNumber, true);
        setPoDocs(prev => ({ ...prev, [poNumber]: doc }));
      }
      if (!doc?.exists) {
        dispatch(showToast({ message: 'PO document not found in OneDrive.', type: 'warning' }));
        return;
      }
      openOneDriveDocInViewer(doc);
    } catch (error) {
      dispatch(showToast({ message: error.message || 'Failed to load PO document.', type: 'danger' }));
    }
  };

  const handleCloseDocumentViewer = () => {
    revokeDocumentViewerUrl();
    setShowDocumentViewer(false);
    setDocumentViewerUrl('');
    setDocumentViewerName('');
  };

  const handleDownloadCurrentDocument = () => {
    if (!documentViewerUrl) return;
    if (documentViewerUrl.startsWith('blob:')) {
      fetch(documentViewerUrl)
        .then(response => response.blob())
        .then(blob => saveAs(blob, documentViewerName || 'document.pdf'))
        .catch(() => window.open(documentViewerUrl, '_blank', 'noopener,noreferrer'));
      return;
    }
    window.open(documentViewerUrl, '_blank', 'noopener,noreferrer');
  };

  const parseShipmentDateToDate = (dateStr) => {
    if (!dateStr) return null;
    // Handles: MM/DD/YYYY or MM/DD/YYYY hh:mm AM/PM (from API), also ISO-like strings
    if (typeof dateStr === 'string' && dateStr.includes('/')) {
      const match = dateStr.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})(?:\s+(\d{1,2}):(\d{2})\s*(AM|PM))?$/i);
      if (!match) return null;
      const [, mm, dd, yyyy, hhRaw, minRaw, ampmRaw] = match;
      const month = mm.padStart(2, '0');
      const day = dd.padStart(2, '0');
      const year = yyyy;
      let hours = hhRaw ? parseInt(hhRaw, 10) : 0;
      const minutes = minRaw ? parseInt(minRaw, 10) : 0;
      const ampm = ampmRaw ? ampmRaw.toUpperCase() : null;
      if (ampm) {
        if (ampm === 'PM' && hours < 12) hours += 12;
        if (ampm === 'AM' && hours === 12) hours = 0;
      }
      const d = new Date(`${year}-${month}-${day}T${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:00`);
      return isNaN(d) ? null : d;
    }
    const d = new Date(dateStr);
    return isNaN(d) ? null : d;
  };

  const formatDateToIsoLocal = (date) => {
    if (!(date instanceof Date) || isNaN(date)) return '';
    const yyyy = date.getFullYear();
    const mm = String(date.getMonth() + 1).padStart(2, '0');
    const dd = String(date.getDate()).padStart(2, '0');
    const hh = String(date.getHours()).padStart(2, '0');
    const min = String(date.getMinutes()).padStart(2, '0');
    const ss = String(date.getSeconds()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}T${hh}:${min}:${ss}`;
  };

  // Prepare vendor options for react-select
  const vendorOptions = vendors.map(vendor => ({
    value: vendor.id,
    label: vendor.vendorName,
  }));

  // Load vendors only when the create shipment form is shown
  useEffect(() => {
    if (!showMappingsUI) {
      dispatch(fetchVendorsWithFilters());
    }
  }, [showMappingsUI, dispatch]);

  /**
   * Vendor/supplier resolved directly by shipment number — the authoritative fallback for a
   * shipment that has no POs merged yet (e.g. brand new, no OS&D report either), where
   * createdShipment.vendorIds is empty because it is only ever populated from merged POs.
   * SP_GetAllReceivingShipments resolves SupplierName correctly regardless of merge state, so this
   * is more reliable here than the vendor picker's UI selection state.
   */
  useEffect(() => {
    const shipmentNumber = createdShipment?.shipmentNumber;
    if (!shipmentNumber) { setShipmentNumberVendorName(''); return; }

    const fromLoadedList = (shipments || []).find(s => s.shipmentNumber === shipmentNumber)?.supplierName;
    if (fromLoadedList) { setShipmentNumberVendorName(fromLoadedList); return; }

    let alive = true;
    ReceivingShipmentService.getAllShipments({ searchText: shipmentNumber })
      .then(res => {
        if (!alive) return;
        const list = res?.status === 200 ? (res.response || []) : [];
        const match = list.find(s => s.shipmentNumber === shipmentNumber);
        setShipmentNumberVendorName(match?.supplierName || '');
      })
      .catch(() => { if (alive) setShipmentNumberVendorName(''); });
    return () => { alive = false; };
  }, [createdShipment?.shipmentNumber, shipments]);

  // Load feature flags when component mounts
  useEffect(() => {
    const loadFeatureFlags = async () => {
      try {
        const response = await FeatureFlagService.isFeatureEnabled('CloseShipment');
        if (response.status === 200) {
          // The API returns a string "true" or "false"
          const isEnabled = response.response === "true";
          setIsCloseShipmentEnabled(isEnabled);
        }
      } catch (error) {
        // Default to false if there's an error
        setIsCloseShipmentEnabled(false);
      }
    };

    loadFeatureFlags();
  }, []);

  // Fetch all POs after shipment is created
  useEffect(() => {
    if (createdShipmentId) {
      // Fetch all POs, not just by vendor
      const fetchAllPOs = async () => {
        try {
          const response = await POService.getAllPOs();
          if (response.status === 200) {
            dispatch({ type: 'po/fetchPOsByVendorIds/fulfilled', payload: response.response });
          }
        } catch (error) {
          // Optionally handle error
        }
      };
      fetchAllPOs();
    }
  }, [createdShipmentId, dispatch]);

  // Fetch POs when vendor changes (use first selected vendor for now)
  useEffect(() => {
    const fetchAllPOs = async () => {
      try {
        const response = await POService.getAllPOs();
        if (response.status === 200) {
          dispatch({ type: 'po/fetchPOsByVendorIds/fulfilled', payload: response.response });
        }
      } catch (error) {
        // Optionally handle error
      }
    };
    if (selectedVendorIds.length > 0) {
      const payload = { vendorIds: selectedVendorIds };
      if (createdShipmentId) payload.receivingShipmentId = createdShipmentId;
      dispatch(fetchPOsByVendorIds(payload));
      setSelectedPOId(null);
      setSelectedPartId(null);
      setSelectedPOIds([]);
      setMultiplePOItems([]);
    } else {
      fetchAllPOs();
      setSelectedPOId(null);
      setSelectedPartId(null);
      setSelectedPOIds([]);
      setMultiplePOItems([]);
    }
  }, [selectedVendorIds, dispatch, createdShipmentId]);

  // Fetch items from multiple POs when selectedPOIds changes
  useEffect(() => {
    const fetchMultiplePOItems = async () => {
      if (selectedPOIds.length > 0) {
        // Only fetch items for merged POs
        const mergedPOIds = createdShipment?.mergedPOs || [];
        const validPOIds = selectedPOIds.filter(poId => mergedPOIds.includes(poId));
        
        if (validPOIds.length === 0) {
          // Don't clear the items - keep showing existing merged PO items
          setMultiplePOItemsLoading(false);
          return;
        }
        
        setMultiplePOItemsLoading(true);
        try {
          const response = await POService.getPOItemsByPOReceivingIds(validPOIds, createdShipmentId || 0);
          if (response.status === 200) {
            setMultiplePOItems(response.response || []);
          } else {
            setMultiplePOItems([]);
          }
        } catch (error) {
          setMultiplePOItems([]);
        } finally {
          setMultiplePOItemsLoading(false);
        }
      } else {
        setMultiplePOItems([]);
      }
    };

    fetchMultiplePOItems();
  }, [selectedPOIds, createdShipmentId, createdShipment?.mergedPOs]);

  const isAsnShipment = isAsnOriginShipment(createdShipment);
  const shipmentParts = createdShipment?.poItems || [];
  const asnShipmentPOIds = Array.from(
    new Set((createdShipment?.mergedPOs?.length ? createdShipment.mergedPOs : shipmentParts.map(item => item.poReceivingID)).filter(Boolean))
  );
  const firstAsnPoId = asnShipmentPOIds[0];
  const filteredPOs = poState.data || [];
  const displayPOs = isAsnShipment
    ? filteredPOs.filter(po => asnShipmentPOIds.some(id => sameReceivingId(id, po.poReceivingID)))
    : filteredPOs;
  const asnPartsForSelectedPos = shipmentParts.filter(item =>
    selectedPOIds.some(id => sameReceivingId(id, item.poReceivingID))
  );
  const asnReceivedParts = asnPartsForSelectedPos.filter(item => Number(item.receivedInThisShipment) > 0);
  const filteredParts = selectedPOIds.length > 0
    ? (isAsnShipment ? (asnReceivedParts.length ? asnReceivedParts : asnPartsForSelectedPos) : multiplePOItems)
    : shipmentParts;
  const filteredReleases = itemReleases || [];

  const selectedPOs = displayPOs.filter(po => selectedPOIds.includes(po.poReceivingID));
  const unselectedPOs = displayPOs.filter(po => !selectedPOIds.includes(po.poReceivingID));
    const mergedPONumbers = (createdShipment?.mergedPOs || [])
    .map(poId => displayPOs.find(po => po.poReceivingID === poId)?.poNumber)
    .filter(Boolean);
  const isPoChecked = (po) => selectedPOIds.some(id => sameReceivingId(id, po.poReceivingID));
  const groupedPOs = [...displayPOs.filter(isPoChecked), ...displayPOs.filter(po => !isPoChecked(po))];

  const filteredPOsBySearch = groupedPOs.filter(po =>
    (po.poNumber || '').toLowerCase().includes(poSearch.toLowerCase())
  );
  const filteredPartsBySearch = filteredParts.filter(part => {
    const q = partsSearch.toLowerCase();
    return (
      (part.itemName || '').toLowerCase().includes(q) ||
      (part.partNumber || '').toLowerCase().includes(q) ||
      (part.description || '').toLowerCase().includes(q)
    );
  });
  const filteredReleasesBySearch = filteredReleases.filter(release => {
    const search = releasesSearch.toLowerCase();
    return (release.requestDate || '').toLowerCase().includes(search)
      || (release.promiseDate || '').toLowerCase().includes(search);
  });
  const mergedPoNumbersKey = displayPOs
    .filter(po => (createdShipment?.mergedPOs || []).some(id => sameReceivingId(id, po.poReceivingID)) && po.poNumber)
    .map(po => po.poNumber)
    .sort()
    .join('|');
  const poNumbersWithDocs = Object.keys(poDocs).filter(poNumber => poDocs[poNumber]?.exists);

  useEffect(() => {
    const shipmentNumber = createdShipment?.shipmentNumber;
    if (!shipmentNumber) {
      setShipmentDoc(null);
      setHasShipmentDoc(false);
      setShipmentDocLoading(false);
      return undefined;
    }

    let cancelled = false;
    const loadShipmentDoc = async () => {
      setShipmentDocLoading(true);
      try {
        const doc = await fetchReceivingDoc();
        if (cancelled) return;
        setShipmentDoc(doc);
        setHasShipmentDoc(Boolean(doc?.exists));
      } catch {
        if (cancelled) return;
        setShipmentDoc({ exists: false, fileName: `${shipmentNumber}.pdf`, fileBase64: null, webUrl: null });
        setHasShipmentDoc(false);
      } finally {
        if (!cancelled) setShipmentDocLoading(false);
      }
    };

    loadShipmentDoc();
    return () => {
      cancelled = true;
    };
  }, [createdShipment?.shipmentNumber]);

  useEffect(() => {
    const shipmentNumber = createdShipment?.shipmentNumber;
    if (!shipmentNumber || !mergedPoNumbersKey) {
      return undefined;
    }

    const poNumbers = mergedPoNumbersKey.split('|').filter(Boolean);
    let cancelled = false;
    const loadPoDocs = async () => {
      const entries = await Promise.all(poNumbers.map(async (poNumber) => {
        try {
          const doc = await ReceivingShipmentService.getShipDoc(shipmentNumber, poNumber, false)
            .then(response => normalizeOneDriveDoc(response, `${shipmentNumber}_${poNumber}.pdf`));
          return [poNumber, doc];
        } catch {
          return [poNumber, { exists: false, fileName: `${shipmentNumber}_${poNumber}.pdf`, fileBase64: null, webUrl: null }];
        }
      }));
      if (cancelled) return;
      setPoDocs(prev => {
        const next = { ...prev };
        entries.forEach(([poNumber, doc]) => {
          next[poNumber] = {
            ...doc,
            fileBase64: prev[poNumber]?.fileBase64 || doc.fileBase64,
          };
        });
        return next;
      });
    };

    loadPoDocs();
    return () => {
      cancelled = true;
    };
  }, [createdShipment?.shipmentNumber, mergedPoNumbersKey]);

  useEffect(() => {
    if (!isAsnShipment || !createdShipmentId) {
      if (!createdShipmentId) asnHydratedShipmentIdRef.current = null;
      return;
    }
    const firstPOId = firstAsnPoId;
    if (firstPOId == null || asnHydratedShipmentIdRef.current === createdShipmentId) return;
    asnHydratedShipmentIdRef.current = createdShipmentId;
    setSelectedPOId(firstPOId);
    setSelectedPOIds([firstPOId]);
    const firstPartId = firstAsnPartForPo(createdShipment?.poItems, firstPOId)?.poItemDetailID || null;
    setSelectedPartId(firstPartId);
    if (firstPartId) {
      dispatch(fetchItemReleases({
        poItemDetailId: firstPartId,
        shipmentId: createdShipmentId,
      }));
    }
  }, [isAsnShipment, createdShipmentId, firstAsnPoId, createdShipment?.poItems, dispatch]);

  const handleVendorSelect = (selectedOptions) => {
    const ids = selectedOptions ? selectedOptions.map(opt => opt.value) : [];
    setSelectedVendorIds(ids);
    setSelectedPOId(null);
    setSelectedPartId(null);
    setSelectedPOIds([]);
    setMultiplePOItems([]);
    // Clear releases when vendor changes
    dispatch({ type: 'po/fetchItemReleases/fulfilled', payload: [] });
    // Clear selected PO data to clear parts
    dispatch({ type: 'po/editPOData/fulfilled', payload: null });
  };

  const handlePOSelect = async (poId) => {
    setSelectedPOId(poId);
    setSelectedPOIds([poId]);
    setMultiplePOItems([]);

    if (isAsnShipment) {
      const firstPartId = firstAsnPartForPo(shipmentParts, poId)?.poItemDetailID || null;
      setSelectedPartId(firstPartId);
      if (firstPartId) {
        dispatch(fetchItemReleases({
          poItemDetailId: firstPartId,
          shipmentId: createdShipmentId || createdShipment?.receivingShipmentID || 0,
        }));
      } else {
        dispatch({ type: 'po/fetchItemReleases/fulfilled', payload: [] });
      }
      return;
    }

    setSelectedPartId(null);
    dispatch({ type: 'po/fetchItemReleases/fulfilled', payload: [] });
    if (!createdShipment?.mergedPOs?.some(id => sameReceivingId(id, poId))) return;
    try {
      const response = await POService.getPOItemsByPOReceivingIds([poId], createdShipmentId || 0);
      setMultiplePOItems(response.status === 200 ? (response.response || []) : []);
    } catch (error) {
      setMultiplePOItems([]);
    }
  };

  const handlePartSelect = (partId) => {
    setSelectedPartId(partId);
    if (!partId) {
      // Clear releases if no part is selected
      dispatch({ type: 'po/fetchItemReleases/fulfilled', payload: [] });
      return;
    }
    // Use the current shipment ID if available, otherwise 0
    const shipmentId = createdShipmentId || createdShipment?.receivingShipmentID || 0;
    dispatch(fetchItemReleases({ poItemDetailId: partId, shipmentId })); // Fetch releases for this part
  };
  const handleViewRelease = (release) => {
    const releaseState = getReleaseReceiveState(release);
    setSelectedRelease(release);
    setReceivingQuantity(
      releaseState.isReceivedInThisShipment ? releaseState.receivedInThisShipment : ''
    );
    setIsReleaseAlreadyReceived(releaseState.isReceivedInThisShipment);
    setIsReleaseVoided(release.isVoided || false);
    setShowReleaseModal(true);
  };
  const handleCloseReleaseModal = () => {
    setShowReleaseModal(false);
    setSelectedRelease(null);
    setReceivingQuantity('');
    setIsReleaseAlreadyReceived(false);
    setIsReleaseVoided(false);
  };

  // Handler for voiding a release
  const handleVoidRelease = async () => {
    if (!selectedRelease) return;
    
    try {
      // Call API to void the release using same fields as save-part-receiving
      const response = await ReceivingShipmentService.voidItem({
        partID: selectedRelease.partId,
        releaseID: selectedRelease.poItemReleaseID,
        receivedQty: selectedRelease.receivedInThisShipment || 0,
        createdBy: '', // backend will set from user
        shipmentID: createdShipmentId,
        poReceivingID: selectedRelease.poReceivingId,
        poItemDetailID: selectedRelease.poItemDetailID,
        poItemReleaseID: selectedRelease.poItemReleaseID
      });
      
      if (response.status === 200) {
        dispatch(showToast({ message: 'Release voided successfully!', type: 'success' }));
        setIsReleaseVoided(true);
        
        // Update local state to reflect voided status
        setMultiplePOItems(prevItems => {
          const updatedItems = prevItems.map(item => {
            if (item.poItemDetailID === selectedRelease.poItemDetailID) {
              return {
                ...item,
                receivedInThisShipment: 0,
                isVoided: true
              };
            }
            return item;
          });
          return updatedItems;
        });
        
        // Force a re-render
        setLastUpdateTime(Date.now());
      } else {
        dispatch(showToast({ message: 'Failed to void release: ' + (response.errorMessage?.message || 'Unknown error'), type: 'danger' }));
      }
    } catch (error) {
      dispatch(showToast({ message: 'Error voiding release: ' + error.message, type: 'danger' }));
    }
  };

  // Handler for voiding a part
  const handleVoidPart = async () => {
    if (!selectedItemForReceiving) return;
    
    try {
      // Call API to void the part using same fields as save-part-receiving
      const response = await ReceivingShipmentService.voidItem({
        partID: selectedItemForReceiving.partId,
        releaseID: 0, // No release for direct part receiving
        receivedQty: selectedItemForReceiving.receivedInThisShipment || 0,
        createdBy: '', // backend will set from user
        shipmentID: createdShipmentId,
        poReceivingID: selectedItemForReceiving.poReceivingID,
        poItemDetailID: selectedItemForReceiving.poItemDetailID,
        poItemReleaseID: 0 // No release for direct part receiving
      });
      
      if (response.status === 200) {
        dispatch(showToast({ message: 'Part voided successfully!', type: 'success' }));
        setIsPartVoided(true);
        
        // Update local state to reflect voided status
        setMultiplePOItems(prevItems => {
          const updatedItems = prevItems.map(item => {
            if (item.poItemDetailID === selectedItemForReceiving.poItemDetailID) {
              return {
                ...item,
                receivedInThisShipment: 0,
                isVoided: true
              };
            }
            return item;
          });
          return updatedItems;
        });
        
        // Force a re-render
        setLastUpdateTime(Date.now());
        handleCloseReceiveModal();
      } else {
        dispatch(showToast({ message: 'Failed to void part.', type: 'danger' }));
      }
    } catch (error) {
      dispatch(showToast({ message: 'Error voiding part: ' + error.message, type: 'danger' }));
    }
  };

  // Handler for creating shipment
  const handleCreateReceivingShipment = async (data) => {
    try {
      setShipmentLoading(true);
      
      // Prepare shipment data
      const shipmentData = {
        Comments: data.comments,
        ShipmentDate: data.shipmentDate,
        VendorIds: selectedVendorIds
      };

      // Call the API to create shipment
      const response = await ReceivingShipmentService.createShipment(shipmentData);
      
      if (response.status === 200) {
        // Use shipment number instead of ID
        const shipmentNumber = response.response.shipmentNumber;
        setCreatedShipmentId(response.response.receivingShipmentID);
        setCreatedShipment(response.response);
        
        // Set vendor IDs from the created shipment
        if (response.response.vendorIds && response.response.vendorIds.length > 0) {
          setSelectedVendorIds(response.response.vendorIds);
        }
        
        // If there are selected POs, merge them into the shipment
        if (selectedPOIds.length > 0 && response.response.receivingShipmentID) {
          const mergeRequest = {
            ReceivingShipmentID: response.response.receivingShipmentID,
            POIds: selectedPOIds
          };
          const mergeResponse = await ReceivingShipmentService.mergePOs(mergeRequest);
          if (mergeResponse.status === 200) {
            // POs merged successfully
          }
        }
        // Show success message and stay in the create/POs/Parts/Releases/Merge UI
        dispatch(showToast({ message: `Shipment created successfully! Shipment Number: ${shipmentNumber}`, type: 'success' }));
        if (onRefreshShipments) {
          onRefreshShipments();
        }
        // Clear releases after creating a shipment
        dispatch({ type: 'po/fetchItemReleases/fulfilled', payload: [] });
      } else {
        dispatch(showToast({ message: 'Failed to create shipment: ' + (response.errorMessage?.message || 'Unknown error'), type: 'danger' }));
      }
    } catch (error) {
      dispatch(showToast({ message: 'Error creating shipment: ' + error.message, type: 'danger' }));
    } finally {
      setShipmentLoading(false);
    }
  };

  const startEditShipmentInfo = () => {
    setEditShipmentComments(createdShipment?.comments || '');
    setEditShipmentDate(parseShipmentDateToDate(createdShipment?.shipmentDate));
    setIsEditingShipmentInfo(true);
  };

  const cancelEditShipmentInfo = () => {
    setIsEditingShipmentInfo(false);
    setEditShipmentComments('');
    setEditShipmentDate(null);
  };

  const handleUpdateReceivingShipment = async () => {
    const shipmentIdToUse = createdShipmentId || createdShipment?.receivingShipmentID;
    if (!shipmentIdToUse) {
      dispatch(showToast({ message: 'No shipment selected to update.', type: 'info' }));
      return;
    }

    setUpdateShipmentLoading(true);
    try {
      const payload = {
        ReceivingShipmentID: shipmentIdToUse,
        Comments: editShipmentComments,
        ShipmentDate: formatDateToIsoLocal(editShipmentDate),
      };
      const response = await ReceivingShipmentService.updateShipment(payload);
      if (response.status === 200) {
        setCreatedShipment(response.response);
        setCreatedShipmentId(response.response?.receivingShipmentID || shipmentIdToUse);
        setIsEditingShipmentInfo(false);
        dispatch(showToast({ message: 'Shipment updated successfully.', type: 'success' }));
        if (typeof onRefreshShipments === 'function') {
          onRefreshShipments();
        }
      } else {
        dispatch(showToast({ message: 'Failed to update shipment: ' + (response.errorMessage?.message || 'Unknown error'), type: 'danger' }));
      }
    } catch (error) {
      dispatch(showToast({ message: 'Error updating shipment: ' + (error.message || 'Unknown error'), type: 'danger' }));
    } finally {
      setUpdateShipmentLoading(false);
    }
  };

  // Handler for merging additional POs into existing shipment
  const handleMergeShipment = async () => {
    if (!createdShipmentId || selectedPOIds.length === 0) {
      dispatch(showToast({ message: 'Please create a shipment first and select POs to merge.', type: 'info' }));
      return;
    }

    try {
      setShipmentLoading(true);
      
      const mergeRequest = {
        ReceivingShipmentID: createdShipmentId,
        POIds: selectedPOIds
      };
      
      const mergeResponse = await ReceivingShipmentService.mergePOs(mergeRequest);
      if (mergeResponse.status === 200) {
        dispatch(showToast({ message: `Successfully merged ${selectedPOIds.length} POs into shipment ${createdShipmentId}`, type: 'success' }));
        setSelectedPOIds([]); // Clear selected POs after merging
        // Re-fetch shipment data to get the updated mergedPOs list and vendor IDs
        const updatedShipmentResponse = await ReceivingShipmentService.getShipmentById(createdShipmentId);
        if (updatedShipmentResponse.status === 200 && updatedShipmentResponse.response) {
            const updatedShipment = updatedShipmentResponse.response;
            setCreatedShipment(updatedShipment);
            
            // Update vendor IDs if they changed
            if (updatedShipment.vendorIds && updatedShipment.vendorIds.length > 0) {
              setSelectedVendorIds(updatedShipment.vendorIds);
            }
            
            // Don't update selectedPOIds here - they should remain cleared after merging
        }
      } else {
        dispatch(showToast({ message: 'Failed to merge POs: ' + (mergeResponse.errorMessage?.message || 'Unknown error'), type: 'danger' }));
      }
    } catch (error) {
      dispatch(showToast({ message: 'Error merging POs: ' + error.message, type: 'danger' }));
    } finally {
      setShipmentLoading(false);
    }
  };

  const handlePOCheck = (poId, checked) => {
    setSelectedPOIds(prev => {
      const newSelectedIds = checked 
        ? [...prev, poId] 
        : prev.filter(id => id !== poId);
      
      // If multiple POs are selected, clear single PO selection
      if (newSelectedIds.length > 0) {
        setSelectedPOId(null);
        dispatch({ type: 'po/editPOData/fulfilled', payload: null });
      }
      
      return newSelectedIds;
    });
  };

  const handlePOUploadIconClick = (poId) => {
    setUploadPOId(poId);
    setUploadFile(null);
    setShowUploadModal(true);
  };

  const handleUploadFileChange = (e) => {
    setUploadFile(e.target.files[0] || null);
  };

  const handleUploadFile = async () => {
    const shipmentIdToUse = createdShipmentId || createdShipment?.receivingShipmentID;
    const shipmentNumber = createdShipment?.shipmentNumber;
    const isShipmentDocument = uploadPOId === 0;

    if (!uploadFile || uploadPOId === null || uploadPOId === undefined || !shipmentIdToUse || !shipmentNumber) {
      dispatch(showToast({ message: 'Please select a file and ensure a shipment is created.', type: 'info' }));
      return;
    }

    let poNumber = '';
    if (!isShipmentDocument) {
      const po = displayPOs.find(p => p.poReceivingID === uploadPOId)
        || filteredPOs.find(p => p.poReceivingID === uploadPOId);
      poNumber = po?.poNumber || '';
      if (!poNumber) {
        dispatch(showToast({ message: 'Could not determine the PO number for this document.', type: 'info' }));
        return;
      }
    }

    try {
      const formData = new FormData();
      formData.append('File', uploadFile);
      formData.append('ShipmentNumber', shipmentNumber);
      if (poNumber) {
        formData.append('PONumber', poNumber);
      }

      const uploadResponse = await ReceivingShipmentService.uploadOneDriveDocument(formData);

      if (uploadResponse.status === 200 && uploadResponse.response) {
        const uploadedName = uploadResponse.response.fileName || uploadFile.name;
        if (isShipmentDocument) {
          setHasShipmentDoc(true);
          setShipmentDoc({
            exists: true,
            fileName: uploadedName,
            fileBase64: null,
            webUrl: uploadResponse.response.webUrl || null,
          });
        } else {
          setPoDocs(prev => ({
            ...prev,
            [poNumber]: {
              exists: true,
              fileName: uploadedName,
              fileBase64: null,
              webUrl: uploadResponse.response.webUrl || null,
            }
          }));
        }
        dispatch(showToast({
          message: uploadResponse.response.message
            || (isShipmentDocument
              ? 'Shipment document uploaded to OneDrive successfully.'
              : 'PO document uploaded to OneDrive successfully.'),
          type: 'success'
        }));
      } else {
        dispatch(showToast({
          message: uploadResponse.errorMessage?.message || 'Failed to upload file to OneDrive.',
          type: 'danger'
        }));
      }
    } catch (error) {
      dispatch(showToast({
        message: `Error uploading ${isShipmentDocument ? 'shipment' : 'PO'} document: ${error.message}`,
        type: 'danger'
      }));
    } finally {
      setShowUploadModal(false);
      setUploadFile(null);
    }
  };

  const handleCloseUploadModal = () => {
    setShowUploadModal(false);
    setUploadFile(null);
    setUploadPOId(null);
  };

  const handleReceiveItem = (item) => {
    setSelectedItemForReceiving(item);
    // Pre-populate with receivedInThisShipment if > 0, else blank
    if (item.receivedInThisShipment && item.receivedInThisShipment > 0) {
      setReceivingQuantity(item.receivedInThisShipment);
    } else {
      setReceivingQuantity('');
    }
    setIsPartVoided(item.isVoided || false);
    setShowReceiveModal(true);
  };

  const handleCloseReceiveModal = () => {
    setShowReceiveModal(false);
    setSelectedItemForReceiving(null);
    setReceivingQuantity('');
    setIsPartVoided(false);
  };

  // Save received quantity for part
  const handleSaveReceiving = async () => {
    if (!selectedItemForReceiving || !receivingQuantity) return;
    
    // Use the correct part ID from the selected item
    const partID = selectedItemForReceiving.poItemDetailID;
    
    if (!partID) {
      dispatch(showToast({ message: 'Error: Could not determine part ID. Please try again.', type: 'danger' }));
      return;
    }
    
    try {
      const response = await ReceivingShipmentService.savePartReceiving({
        partID: partID,
        releaseID: 0,
        receivedQty: parseFloat(receivingQuantity),
        createdBy: '', // backend will set from user
        shipmentID: createdShipmentId // Add shipment ID
      });
      if (response.status === 200) {
        dispatch(showToast({ message: 'Received quantity saved successfully!', type: 'success' }));
        
        // Update the local state to show the received badge and print button immediately
        setMultiplePOItems(prevItems => {
          const updatedItems = prevItems.map(item => {
            if (item.poItemDetailID === partID) {
              const newReceivedQty = (item.receivedInThisShipment || 0) + parseFloat(receivingQuantity);
              return {
                ...item,
                receivedInThisShipment: newReceivedQty
              };
            }
            return item;
          });
          return updatedItems;
        });
        
        // Force a re-render by updating a timestamp
        setLastUpdateTime(Date.now());
      } else {
        dispatch(showToast({ message: 'Failed to save received quantity.', type: 'danger' }));
      }
    } catch (error) {
      dispatch(showToast({ message: 'Error saving received quantity: ' + error.message, type: 'danger' }));
    }
    handleCloseReceiveModal();
  };

  // Save received quantity for release
  const handleSaveReleaseReceiving = async () => {
    if (!selectedRelease || !receivingQuantity) return;

    const releaseState = getReleaseReceiveState(selectedRelease);
    if (!releaseState.canMarkAsReceived) {
      dispatch(showToast({
        message: releaseState.isFullyReceived
          ? 'This release is already fully received.'
          : 'This release has already been received in this shipment.',
        type: 'warning',
      }));
      return;
    }

    const enteringQty = parseFloat(receivingQuantity);
    if (releaseState.remainingQty > 0 && enteringQty > releaseState.remainingQty) {
      dispatch(showToast({
        message: `Cannot receive more than the remaining quantity (${releaseState.remainingQty}).`,
        type: 'warning',
      }));
      return;
    }

    try {
      const response = await ReceivingShipmentService.savePartReceiving({
        partID: selectedRelease.poItemDetailID, // Use the correct field for releases
        releaseID: selectedRelease.poItemReleaseID,
        receivedQty: parseFloat(receivingQuantity),
        createdBy: '',
        shipmentID: createdShipmentId // Add shipment ID
      });
      if (response.status === 200) {
        dispatch(showToast({ message: 'Release received quantity saved successfully!', type: 'success' }));
        
        // Update the local state to show the received badge and print button immediately
        setMultiplePOItems(prevItems => {
          const updatedItems = prevItems.map(item => {
            if (item.poItemDetailID === selectedRelease.poItemDetailID) {
              const newReceivedQty = (item.receivedInThisShipment || 0) + parseFloat(receivingQuantity);
              return {
                ...item,
                receivedInThisShipment: newReceivedQty
              };
            }
            return item;
          });
          return updatedItems;
        });

        // Refresh releases so received qty + Print Labels button updates
        const shipmentId = createdShipmentId || createdShipment?.receivingShipmentID || 0;
        const poItemDetailId = selectedRelease?.poItemDetailID || selectedPartId;
        if (poItemDetailId) {
          dispatch(fetchItemReleases({ poItemDetailId, shipmentId }));
        }

        // Force a re-render by updating a timestamp
        setLastUpdateTime(Date.now());
      } else {
        dispatch(showToast({ message: 'Failed to save release received quantity.', type: 'danger' }));
      }
    } catch (error) {
      dispatch(showToast({ message: 'Error saving release received quantity: ' + error.message, type: 'danger' }));
    }
    handleCloseReleaseModal();
  };

  // List filtering is server-side (header part/PO + toolbar supplier/shipment number + filter panel).
  const filteredShipments = shipments || [];

  // Handler for viewing/editing a shipment from tile/table
  const handleViewShipment = async (shipment) => {
    try {
      const response = await ReceivingShipmentService.getShipmentById(shipment.receivingShipmentID);
      if (response.status === 200 && response.response) {
        const shipmentData = response.response;
        setCreatedShipmentId(shipmentData.receivingShipmentID);
        setCreatedShipment(shipmentData);
        setShowMappingsUI(false);
        setSelectedPOId(null);
        setSelectedPartId(null);
        setSelectedPOIds([]);
        setMultiplePOItems([]);
        asnHydratedShipmentIdRef.current = null;
        
        if (shipmentData.vendorIds?.length > 0) {
          setSelectedVendorIds(shipmentData.vendorIds);
          dispatch(fetchPOsByVendorIds({
            vendorIds: shipmentData.vendorIds,
            receivingShipmentId: shipmentData.receivingShipmentID,
          }));
          if (!isAsnOriginShipment(shipmentData) && shipmentData.mergedPOs?.length > 0) {
            setSelectedPOIds(shipmentData.mergedPOs);
          }
        }
        dispatch({ type: 'po/fetchItemReleases/fulfilled', payload: [] });
      } else {
        dispatch(showToast({ message: 'Failed to fetch shipment details.', type: 'danger' }));
      }
    } catch (error) {
      dispatch(showToast({ message: 'Error fetching shipment details: ' + error.message, type: 'danger' }));
    }
  };

  // Handler to reset all shipment-related state for a new entry
  const handleAddNewShipment = () => {
    setCreatedShipmentId(null);
    setCreatedShipment(null);
    setSelectedVendorIds([]);
    setSelectedPOId(null);
    setSelectedPartId(null);
    setSelectedPOIds([]);
    setMultiplePOItems([]);
    asnHydratedShipmentIdRef.current = null;
    setShowMappingsUI(false); // Show the create shipment form
    setReceivingQuantity('');
    setShowReceiveModal(false);
    setShowReleaseModal(false);
    setSelectedRelease(null);
    setSelectedItemForReceiving(null);
    setUploadPOId(null);
    setUploadFile(null);
    setPOSearch('');
    setPartsSearch('');
    setReleasesSearch('');
    setHasShipmentDoc(false);
    setHasPODoc(false);
    setShipmentDoc(null);
    setPoDocs({});
    // Optionally clear Redux state for POs and releases
    dispatch({ type: 'po/fetchPOsByVendorIds/fulfilled', payload: [] });
    dispatch({ type: 'po/editPOData/fulfilled', payload: null });
    dispatch({ type: 'po/fetchItemReleases/fulfilled', payload: [] });
  };

  // Expose the handleAddNewShipment function to parent component
  useImperativeHandle(ref, () => ({
    handleAddNewShipment
  }));

  // Helper function to convert MM/DD/YYYY to YYYY-MM-DD for HTML date input
  const convertDateForHTMLInput = (dateStr) => {
    if (!dateStr) return '';
    if (dateStr.includes('/')) {
      const [month, day, year] = dateStr.split('/');
      if (!year || !month || !day) return '';
      return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
    }
    return dateStr; // Already in YYYY-MM-DD format
  };

  // Add handlers for new buttons
  const handleCloseShipment = async () => {
    if (!createdShipmentId) {
      dispatch(showToast({ message: 'No shipment selected to close.', type: 'info' }));
      return;
    }
    try {
      setShipmentLoading(true);
      const closeResponse = await ReceivingShipmentService.closeShipment(createdShipmentId);
      if (closeResponse.status === 200) {
        dispatch(showToast({ message: 'Shipment closed successfully!', type: 'success' }));
        // Refresh shipment data
        const updatedShipmentResponse = await ReceivingShipmentService.getShipmentById(createdShipmentId);
        if (updatedShipmentResponse.status === 200 && updatedShipmentResponse.response) {
          setCreatedShipment(updatedShipmentResponse.response);
        }
      } else {
        dispatch(showToast({ message: 'Failed to close shipment: ' + (closeResponse.errorMessage?.message || 'Unknown error'), type: 'danger' }));
      }
    } catch (error) {
      dispatch(showToast({ message: 'Error closing shipment: ' + error.message, type: 'danger' }));
    } finally {
      setShipmentLoading(false);
    }
  };
  const resolveSupplierName = (item) => {
    const fromItem = item?.vendorName || item?.supplierName || item?.supName;
    if (fromItem) return String(fromItem).trim();
    const po = displayPOs.find(p => p.poReceivingID === item?.poReceivingID);
    if (po?.vendorName) return String(po.vendorName).trim();
    if (selectedVendorIds.length > 0 && vendors.length > 0) {
      return selectedVendorIds
        .map(id => vendors.find(v => v.id === id)?.vendorName)
        .filter(Boolean)
        .join(', ');
    }
    return '';
  };
  const defaultSupplierName = selectedVendorIds
    .map(id => vendors.find(v => v.id === id)?.vendorName)
    .filter(Boolean)
    .join(', ');
  // Vendor name tied to the loaded shipment record itself (createdShipment.vendorIds is always
  // populated by GetShipmentById), rather than the vendor-select's transient UI state — so it is
  // correct as soon as a shipment is loaded, not only after the user touches the vendor picker.
  // This can still come back empty (vendorIds is only populated from merged POs, and/or the
  // client-side vendors list may not have loaded/contain that id) — in which case fall back to the
  // direct-by-shipment-number lookup, which is resolved server-side and doesn't depend on the
  // vendors list at all.
  const vendorIdsResolvedName = (createdShipment?.vendorIds || [])
    .map(id => vendors.find(v => String(v.id) === String(id))?.vendorName)
    .filter(Boolean)
    .join(', ');
  const shipmentVendorName = vendorIdsResolvedName || shipmentNumberVendorName || defaultSupplierName;
  // Handler for printing labels
  const handlePrintLabelsClick = (part) => {
    setPrintModalPart(part);
    setShowPrintModal(true);
  };

  const handlePrintModalClose = () => {
    setShowPrintModal(false);
    setPrintModalPart(null);
  };

  const handlePrintModalSubmit = (formData) => {
    // You can add additional logic here if needed
    // The modal now handles the API call internally
    setShowPrintModal(false);
  };

  const handleDemergePOs = async () => {
    if (!createdShipmentId || selectedPOIds.length === 0) {
      dispatch(showToast({ message: 'Please create a shipment first and select POs to demerge.', type: 'info' }));
      return;
    }
    try {
      setShipmentLoading(true);
      const demergeRequest = {
        ReceivingShipmentID: createdShipmentId,
        POIds: selectedPOIds
      };
      const demergeResponse = await ReceivingShipmentService.deMergePOs(demergeRequest);
      if (demergeResponse.status === 200) {
        dispatch(showToast({ message: `Successfully demerged ${selectedPOIds.length} POs from shipment ${createdShipmentId}`, type: 'success' }));
        setSelectedPOIds([]); // Clear selected POs after demerging
        // Re-fetch shipment data to get the updated mergedPOs list and vendor IDs
        const updatedShipmentResponse = await ReceivingShipmentService.getShipmentById(createdShipmentId);
        if (updatedShipmentResponse.status === 200 && updatedShipmentResponse.response) {
          const updatedShipment = updatedShipmentResponse.response;
          setCreatedShipment(updatedShipment);
          // Update vendor IDs if they changed
          if (updatedShipment.vendorIds && updatedShipment.vendorIds.length > 0) {
            setSelectedVendorIds(updatedShipment.vendorIds);
          }
          // Don't update selectedPOIds here - they should remain cleared after demerging
        }
      } else {
        dispatch(showToast({ message: 'Failed to demerge POs: ' + (demergeResponse.errorMessage?.message || 'Unknown error'), type: 'danger' }));
      }
    } catch (error) {
      dispatch(showToast({ message: 'Error demerging POs: ' + error.message, type: 'danger' }));
    } finally {
      setShipmentLoading(false);
    }
  };

  // Handler for printing pick ticket
  const handlePrintPickTicket = () => {
    if (!createdShipmentId) {
      dispatch(showToast({ message: 'Please create a shipment first.', type: 'info' }));
      return;
    }
    
    // Show the print pick ticket modal
    setShowPrintPickTicketModal(true);
  };

  // If props change (e.g., from navigation), update local state
  useEffect(() => {
    if (propCreatedShipmentId) setCreatedShipmentId(propCreatedShipmentId);
    if (propCreatedShipment) setCreatedShipment(propCreatedShipment);
  }, [propCreatedShipment, propCreatedShipmentId]);

  // Clear releases when showMappingsUI becomes true
  useEffect(() => {
    if (showMappingsUI) {
      dispatch({ type: 'po/fetchItemReleases/fulfilled', payload: [] });
    }
  }, [showMappingsUI, dispatch]);

  // Determine if the selected PO is merged
  const mergedPOIds = createdShipment?.mergedPOs || [];
  // For existing shipments with merged POs, isPOMerged should be true even if no specific PO is selected
  const isSelectedPOMerged = mergedPOIds.length > 1 || (selectedPOId && mergedPOIds.includes(selectedPOId));

  return (
    <div>
      {showReport ? (
        <div>
          <button className="btn btn-light mb-3" onClick={() => setShowReport(false)}>
            Back to Mappings
          </button>
          <MappingsReport />
        </div>
      ) : (
        <>
          <div className="card table-list-card">
            <div className="card-body">
              {!showMappingsUI && (
                <>
                  <div style={{ padding: '32px', margin: '16px 0' }}>
                    {!createdShipmentId ? (
                      <MappingsCreateReceiving
                        onCreate={handleCreateReceivingShipment}
                        showButton={true}
                        comments={createdShipment?.comments}
                        shipmentDate={createdShipment?.shipmentDate}
                        shipmentNumber={createdShipment?.shipmentNumber}
                      />
                    ) : (
                      <>
                        <div className="row mb-3 align-items-end">
                          <div className="col-md-8">
                            <div className="mb-2">
                              <div className="alert alert-success p-2 mb-2 w-100">
                                <strong>Shipment Number:</strong> {createdShipment?.shipmentNumber}
                              </div>
                            </div>
                            <div className="row">
                              <div className="col-md-6 mb-2">
                                <label className="form-label"><strong>Comments</strong></label>
                                {isEditingShipmentInfo ? (
                                  <textarea
                                    className="form-control"
                                    value={editShipmentComments}
                                    onChange={e => setEditShipmentComments(e.target.value)}
                                    rows={2}
                                  />
                                ) : (
                                  <textarea
                                    className="form-control"
                                    value={createdShipment?.comments || ''}
                                    disabled
                                    rows={2}
                                  />
                                )}
                              </div>
                              <div className="col-md-6 mb-2">
                                <label className="form-label"><strong>Date</strong></label>
                                {isEditingShipmentInfo ? (
                                  <DatePicker
                                    selected={editShipmentDate}
                                    onChange={d => setEditShipmentDate(d)}
                                    showTimeSelect
                                    timeIntervals={5}
                                    timeCaption="Time"
                                    dateFormat="MM/dd/yyyy h:mm aa"
                                    className="form-control"
                                    placeholderText="MM/DD/YYYY hh:mm AM/PM"
                                    autoComplete="off"
                                  />
                                ) : (
                                  <input
                                    type="text"
                                    className="form-control"
                                    value={createdShipment?.shipmentDate || ''}
                                    disabled
                                    readOnly
                                  />
                                )}
                              </div>
                            </div>
                          </div>
                          <div className="col-md-4">
                            <div className="d-flex justify-content-end gap-2 mb-2">
                              {!createdShipment?.isClosed && (
                                <>
                                  {!isEditingShipmentInfo ? (
                                    <button
                                      className="btn btn-outline-primary"
                                      type="button"
                                      onClick={startEditShipmentInfo}
                                    >
                                      Edit Shipment
                                    </button>
                                  ) : (
                                    <>
                                      <button
                                        className="btn btn-primary"
                                        type="button"
                                        onClick={handleUpdateReceivingShipment}
                                        disabled={updateShipmentLoading}
                                      >
                                        {updateShipmentLoading ? 'Saving...' : 'Save'}
                                      </button>
                                      <button
                                        className="btn btn-light"
                                        type="button"
                                        onClick={cancelEditShipmentInfo}
                                        disabled={updateShipmentLoading}
                                      >
                                        Cancel
                                      </button>
                                    </>
                                  )}
                                </>
                              )}
                              {!hasShipmentDoc ? (
                                <button
                                  className="btn btn-info"
                                  type="button"
                                  onClick={() => {
                                    setUploadPOId(0);
                                    setShowUploadModal(true);
                                  }}
                                >
                                  Upload Shipment Document
                                </button>
                              ) : (
                                <button
                                  className="btn btn-outline-success"
                                  type="button"
                                  style={{ marginRight: 8 }}
                                  onClick={handleViewUploadedDocument}
                                  disabled={shipmentDocLoading}
                                >
                                  <i className="fa fa-eye" style={{ marginRight: 6 }} />
                                  {shipmentDocLoading ? 'Loading...' : 'View Uploaded Document'}
                                </button>
                              )}
                              <button
                                className="btn btn-outline-warning"
                                type="button"
                                onClick={() => setShowOsdModal(true)}
                              >
                                OS&amp;D
                              </button>

                              {isCloseShipmentEnabled && !createdShipment?.isClosed && (
                                <button
                                  className="btn btn-danger"
                                  type="button"
                                  onClick={handleCloseShipment}
                                >
                                  Close Receipt
                                </button>
                              )}
                            </div>
                            <div className="mb-2">
                              <label className="form-label mb-2"><strong>Vendor</strong></label>
                              <Select
                                isMulti
                                options={vendorOptions}
                                value={vendorOptions.filter(opt => selectedVendorIds.includes(opt.value))}
                                onChange={handleVendorSelect}
                                isLoading={vendorsLoading}
                                styles={customSelectStyles}
                                placeholder="Select Vendor(s)"
                              />
                            </div>
                          </div>
                        </div>
                        <div className="row g-4">
                          <div className="col-md-4">
                            <h5 className="mb-3">All Purchase Orders</h5>
                            <div className="mb-2">
                              <input
                                type="text"
                                className="form-control"
                                placeholder="Search Purchase Orders..."
                                value={poSearch}
                                onChange={e => setPOSearch(e.target.value)}
                              />
                            </div>
                            {poState.loading ? (
                              <LoadingSpinner />
                            ) : (
                              <div className="mappings-scrollable-list">
                                <POList
                                  data={createdShipment?.isClosed ? 
                                    (filteredPOsBySearch.filter(po => (createdShipment?.mergedPOs || []).includes(po.poReceivingID))) : 
                                    filteredPOsBySearch}
                                  selectedId={selectedPOId}
                                  onSelect={handlePOSelect}
                                  selectedPOIds={selectedPOIds}
                                  onPOCheck={handlePOCheck}
                                  onPOUploadIconClick={handlePOUploadIconClick}
                                  onPODocumentView={handleViewPODocument}
                                  mergedPOIds={createdShipment?.mergedPOs || []}
                                  poNumbersWithDocs={poNumbersWithDocs}
                                  isShipmentClosed={createdShipment?.isClosed || false}
                                />

                              </div>
                            )}
                          </div>
                          <div className="col-md-4">
                            <h5 className="mb-3">Parts</h5>
                            {(!createdShipment?.mergedPOs || createdShipment.mergedPOs.length === 0) ? (
                              <div className="alert alert-warning">
                                Please merge at least one PO to this shipment to view and receive parts.
                              </div>
                            ) : (
                              <>
                                <div className="mb-2 d-flex justify-content-between align-items-center">
                                  <input
                                    type="text"
                                    className="form-control me-2"
                                    style={{ flex: '1', maxWidth: '200px' }}
                                    placeholder="Search Parts..."
                                    value={partsSearch}
                                    onChange={e => setPartsSearch(e.target.value)}
                                  />
                                  <button
                                    className="btn btn-primary"
                                    type="button"
                                    onClick={handlePrintPickTicket}
                                    disabled={shipmentLoading}
                                    title="Print Pick Ticket"
                                    style={{ whiteSpace: 'nowrap' }}
                                  >
                                    {shipmentLoading ? (
                                      <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                    ) : (
                                      <i className="fa fa-print me-2"></i>
                                    )}
                                    Print Pick Ticket
                                  </button>
                                </div>
                                {multiplePOItemsLoading ? (
                                  <LoadingSpinner />
                                ) : (
                                  <div className="mappings-scrollable-list">
                                    <PartsList
                                      key={`parts-${lastUpdateTime}`}
                                      data={filteredPartsBySearch}
                                      selectedId={selectedPartId}
                                      onSelect={handlePartSelect}
                                      onReceiveItem={handleReceiveItem}
                                      isShipmentClosed={createdShipment?.isClosed || false}
                                      isPOMerged={isSelectedPOMerged}
                                      receivingShipmentId={createdShipmentId ? createdShipmentId.toString() : ''}
                                      supplierName={defaultSupplierName}
                                    />
                                  </div>
                                )}
                              </>
                            )}
                          </div>
                          <div className="col-md-4">
                            <h5 className="mb-3">Release Delivery Timelines</h5>
                            <div className="mb-2">
                              <input
                                type="text"
                                className="form-control"
                                placeholder="Search Releases..."
                                value={releasesSearch}
                                onChange={e => setReleasesSearch(e.target.value)}
                              />
                            </div>
                            {itemReleasesLoading ? (
                              <LoadingSpinner />
                            ) : (
                              <div className="mappings-scrollable-list">
                                <ReleaseList 
                                  data={filteredReleasesBySearch} 
                                  loading={itemReleasesLoading} 
                                  onView={handleViewRelease}
                                  isShipmentClosed={createdShipment?.isClosed || false}
                                  receivingShipmentId={createdShipmentId ? createdShipmentId.toString() : ''}
                                  supplierName={defaultSupplierName}
                                />
                              </div>
                            )}
                          </div>
                        </div>
                        <div className="mb-4 d-flex justify-content-between align-items-center">
                          {createdShipment?.isClosed && (
                            <div className="alert alert-info mb-0">
                              <i className="fa fa-info-circle me-2"></i>
                              Shipment is closed. Only merged POs are displayed.
                            </div>
                          )}
                          {!createdShipment?.isClosed && (
                            <div>
                              <button
                                className="btn btn-warning me-2"
                                type="button"
                                disabled={selectedPOIds.length === 0}
                                onClick={handleMergeShipment}
                              >
                                Add POs
                              </button>
                              <button
                                className="btn btn-outline-warning"
                                type="button"
                                onClick={handleDemergePOs}
                              >
                                Remove POs
                              </button>
                            </div>
                          )}
                        </div>
                      </>
                    )}
                  </div>
                </>
              )}
              {showMappingsUI && (
                <>
                  <div className="table-top">
                    <MappingsSearch />
                  </div>
                  {filterVisible && <MappingsFilter />}
                  {tileView ? (
                    <MappingsTile shipments={filteredShipments} loading={shipmentsLoading} onViewShipment={handleViewShipment} />
                  ) : (
                    <MappingsTable shipments={filteredShipments} loading={shipmentsLoading} onViewShipment={handleViewShipment} />
                  )}
                </>
              )}
            </div>
          </div>
          {/* Modals for receiving quantity and release details */}
          <Modal show={showReceiveModal} onHide={handleCloseReceiveModal} centered>
            <Modal.Header closeButton>
              <Modal.Title>
                {isPartVoided ? 'View Voided Part' : 
                 selectedItemForReceiving?.receivedInThisShipment > 0 ? 'View Received Item' : 'Mark Item as Received'}
                {selectedItemForReceiving && (
                  <div className="mt-2">
                    {isPartVoided ? (
                      <span className="badge bg-danger">Voided</span>
                    ) : selectedItemForReceiving.receivedInThisShipment > 0 ? (
                      <span className="badge bg-success"><i className="fa fa-check-circle me-1"></i>Received</span>
                    ) : (
                      <span className="badge bg-secondary">Not Received</span>
                    )}
                  </div>
                )}
              </Modal.Title>
            </Modal.Header>
            <Modal.Body>
              {selectedItemForReceiving && (
                <>
                  <div className="mb-2"><strong>Part Number:</strong> {selectedItemForReceiving.partNumber || '—'}</div>
                  <div className="mb-2"><strong>Item Name:</strong> {selectedItemForReceiving.itemName}</div>
                  <div className="mb-2"><strong>Requested Quantity:</strong> {selectedItemForReceiving.quantityRequested}</div>
                  <div className="mb-2"><strong>Total Quantity Received:</strong> {selectedItemForReceiving.quantityReceived || 0}</div>
                  
                  {isPartVoided ? (
                    // Show voided status when part is voided
                    <div className="mb-2">
                      <div className="alert alert-danger">
                        <i className="fa fa-times-circle me-2"></i>
                        Part Voided: {receivingQuantity} units have been voided
                      </div>
                      <div className="mb-2">
                        <label><strong>Voided Quantity:</strong></label>
                        <input
                          type="number"
                          className="form-control"
                          value={receivingQuantity}
                          disabled={true}
                          style={{ backgroundColor: '#f8f9fa' }}
                        />
                      </div>
                      <small className="text-muted">This part has been voided and cannot be modified</small>
                    </div>
                  ) : selectedItemForReceiving.receivedInThisShipment > 0 ? (
                    // Show received status when already received
                    <div className="mb-2">
                      <div className="alert alert-success">
                        <i className="fa fa-check-circle me-2"></i>
                        Already Received: {receivingQuantity} units
                      </div>
                      <div className="mb-2">
                        <label><strong>Receiving Quantity:</strong></label>
                        <input
                          type="number"
                          className="form-control"
                          value={receivingQuantity}
                          disabled={true}
                          style={{ backgroundColor: '#f8f9fa' }}
                        />
                      </div>
                      <small className="text-muted">Quantity cannot be changed once received</small>
                    </div>
                  ) : (
                    // Show input field when not yet received
                    <div className="mb-2">
                      <label><strong>Receiving Quantity:</strong></label>
                      <input
                        type="number"
                        className="form-control"
                        value={receivingQuantity}
                        onChange={e => setReceivingQuantity(e.target.value)}
                        min={0}
                        placeholder="Enter receiving quantity"
                        onKeyDown={e => {
                          if (e.key === 'Enter') handleSaveReceiving();
                        }}
                      />
                      {(() => {
                        const requestedQty = Number(selectedItemForReceiving.quantityRequested) || 0;
                        const alreadyReceived = Number(selectedItemForReceiving.quantityReceived) || 0;
                        const enteringQty = Number(receivingQuantity) || 0;
                        const willExceed = requestedQty > 0 && enteringQty > 0 && (alreadyReceived + enteringQty) > requestedQty;
                        if (!willExceed) return null;
                        return (
                          <div className="alert alert-warning mt-2 mb-0">
                            <i className="fa fa-exclamation-triangle me-2"></i>
                            Entered quantity will exceed requested quantity. New total would be <strong>{alreadyReceived + enteringQty}</strong> (requested <strong>{requestedQty}</strong>).
                          </div>
                        );
                      })()}
                    </div>
                  )}
                </>
              )}
            </Modal.Body>
            <Modal.Footer>
              <Button variant="secondary" onClick={handleCloseReceiveModal}>Close</Button>
              {!isPartVoided && selectedItemForReceiving?.receivedInThisShipment > 0 && (
                <Button 
                  variant="danger" 
                  onClick={handleVoidPart}
                >
                  <i className="fa fa-times me-2"></i>
                  Mark as Void
                </Button>
              )}
              {!isPartVoided && selectedItemForReceiving?.receivedInThisShipment === 0 && (
                <Button 
                  variant="success" 
                  onClick={handleSaveReceiving}
                  disabled={!receivingQuantity || receivingQuantity <= 0}
                >
                  Mark as Received
                </Button>
              )}
            </Modal.Footer>
          </Modal>
          <Modal show={showReleaseModal} onHide={handleCloseReleaseModal} centered>
            <Modal.Header closeButton>
              <Modal.Title>
                {isReleaseVoided ? 'View Voided Release' : 
                 isReleaseAlreadyReceived ? 'View Received Item' : 'Release Details'}
                {selectedRelease && (
                  <div className="mt-2">
                    {(() => {
                      const releaseState = getReleaseReceiveState(selectedRelease);

                      if (isReleaseVoided) {
                        return <span className="badge bg-danger">Voided</span>;
                      } else if (releaseState.isFullyReceived) {
                        return <span className="badge bg-success"><i className="fa fa-check-circle me-1"></i>Fully Received</span>;
                      } else if (isReleaseAlreadyReceived) {
                        return <span className="badge bg-warning"><i className="fa fa-clock me-1"></i>Partially Received</span>;
                      } else {
                        return <span className="badge bg-secondary">Not Received</span>;
                      }
                    })()}
                  </div>
                )}
              </Modal.Title>
            </Modal.Header>
            <Modal.Body>
              {selectedRelease && (
                <>
                  <div className="mb-2">
                    <strong>Part Number:</strong>{' '}
                    {selectedRelease.partNumber
                      || multiplePOItems.find(
                        p => p.poItemDetailID === selectedRelease.poItemDetailID
                      )?.partNumber
                      || '—'}
                  </div>
                  <div className="mb-2"><strong>Name:</strong> {selectedRelease.itemName}</div>
                  <div className="mb-2"><strong>Request Date:</strong> {selectedRelease.requestDate}</div>
                  <div className="mb-2"><strong>Promise Date:</strong> {selectedRelease.promiseDate}</div>
                  <div className="mb-2"><strong>Requested Quantity:</strong> {selectedRelease.requestedQuantity}</div>
                  <div className="mb-2"><strong>Quantity previously received:</strong> {selectedRelease.receivedQuantity || 0}</div>
                  <div className="mb-2"><strong>Comment:</strong> {selectedRelease.comment}</div>
                  
                  {isReleaseVoided ? (
                    // Show voided status when release is voided
                    <div className="mb-2">
                      <div className="alert alert-danger">
                        <i className="fa fa-times-circle me-2"></i>
                        Release Voided: {receivingQuantity} units have been voided
                      </div>
                      <div className="mb-2">
                        <label><strong>Voided Quantity:</strong></label>
                        <input
                          type="number"
                          className="form-control"
                          value={receivingQuantity}
                          disabled={true}
                          style={{ backgroundColor: '#f8f9fa' }}
                        />
                      </div>
                      <small className="text-muted">This release has been voided and cannot be modified</small>
                    </div>
                  ) : isReleaseAlreadyReceived ? (
                    // Show received status when already received in this shipment
                    <div className="mb-2">
                      <div className="alert alert-success">
                        <i className="fa fa-check-circle me-2"></i>
                        Already Received: {receivingQuantity} units
                      </div>
                      <div className="mb-2">
                        <label><strong>Receiving Quantity:</strong></label>
                        <input
                          type="number"
                          className="form-control"
                          value={receivingQuantity}
                          disabled={true}
                          style={{ backgroundColor: '#f8f9fa' }}
                        />
                      </div>
                      <small className="text-muted">Quantity cannot be changed once received</small>
                    </div>
                  ) : (() => {
                    const releaseState = getReleaseReceiveState(selectedRelease);

                    if (releaseState.isFullyReceived) {
                      return null;
                    }

                    return (
                    <div className="mb-2">
                      <label><strong>Receiving Quantity:</strong></label>
                      <input
                        type="number"
                        className="form-control"
                        value={receivingQuantity}
                        onChange={e => setReceivingQuantity(e.target.value)}
                        min={0}
                        max={releaseState.remainingQty > 0 ? releaseState.remainingQty : undefined}
                        placeholder={
                          releaseState.remainingQty > 0 && releaseState.remainingQty < releaseState.requestedQty
                            ? `Enter receiving quantity (up to ${releaseState.remainingQty} remaining)`
                            : 'Enter receiving quantity'
                        }
                        onKeyDown={e => {
                          if (e.key === 'Enter') handleSaveReleaseReceiving();
                        }}
                      />
                      {releaseState.remainingQty > 0 && releaseState.remainingQty < releaseState.requestedQty && (
                        <small className="text-muted d-block mt-1">
                          {releaseState.receivedQty} of {releaseState.requestedQty} already received on other shipment(s).
                          Remaining: {releaseState.remainingQty}.
                        </small>
                      )}
                      {(() => {
                        const enteringQty = Number(receivingQuantity) || 0;
                        const willExceed = releaseState.remainingQty > 0
                          && enteringQty > 0
                          && enteringQty > releaseState.remainingQty;
                        if (!willExceed) return null;
                        return (
                          <div className="alert alert-warning mt-2 mb-0">
                            <i className="fa fa-exclamation-triangle me-2"></i>
                            Entered quantity exceeds the remaining quantity of <strong>{releaseState.remainingQty}</strong>.
                          </div>
                        );
                      })()}
                    </div>
                    );
                  })()}
                </>
              )}
            </Modal.Body>
            <Modal.Footer>
              <Button variant="warning" onClick={handleCloseReleaseModal}>Close</Button>
              {!isReleaseVoided && isReleaseAlreadyReceived && (
                <Button variant="danger" onClick={handleVoidRelease}>
                  <i className="fa fa-times me-2"></i>
                  Mark as Void
                </Button>
              )}
              {!isReleaseVoided && selectedRelease && getReleaseReceiveState(selectedRelease).canMarkAsReceived && (
                <Button
                  variant="success"
                  onClick={handleSaveReleaseReceiving}
                  disabled={!receivingQuantity || Number(receivingQuantity) <= 0}
                >
                  Mark as Received
                </Button>
              )}
            </Modal.Footer>
          </Modal>
          {/* Upload receiving document to OneDrive */}
          <Modal show={showUploadModal} onHide={handleCloseUploadModal} centered>
            <Modal.Header closeButton>
              <Modal.Title>{uploadPOId === 0 ? 'Upload Shipment Document' : 'Upload PO Document'}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
              <input
                type="file"
                className="form-control"
                onChange={handleUploadFileChange}
              />
            </Modal.Body>
            <Modal.Footer>
              <Button variant="secondary" onClick={handleCloseUploadModal}>Cancel</Button>
              <Button variant="primary" onClick={handleUploadFile} disabled={!uploadFile}>Upload</Button>
            </Modal.Footer>
          </Modal>
          {/* Print Pick Ticket Modal */}
          <MappingsPrintPickTicketModal
            show={showPrintPickTicketModal}
            onClose={() => setShowPrintPickTicketModal(false)}
            onSubmit={(formData) => {
              // Handle successful submission
              setShowPrintPickTicketModal(false);
            }}
            shipmentData={{
              receivingShipmentID: createdShipmentId,
              shipmentNumber: createdShipment?.shipmentNumber,
              shipmentDate: createdShipment?.shipmentDate,
              comments: createdShipment?.comments,
              vendorIds: createdShipment?.vendorIds,
              mergedPOs: createdShipment?.mergedPOs,
              poItems: createdShipment?.poItems
            }}
            userId={localStorage.getItem('UserId') || ''}
          />

          {/* OS&D (Over, Short & Damaged) Report Modal */}
          {showOsdModal && (
            <MappingsOSDReportModal
              receivingShipmentId={createdShipmentId}
              shipment={createdShipment}
              vendorName={shipmentVendorName}
              poNumbers={mergedPONumbers}
              parts={filteredPartsBySearch}
              onClose={() => setShowOsdModal(false)}
              onSaved={() => setShowOsdModal(false)}
            />
          )}

          {/* Print Labels Modal */}
          <MappingsPrintModal
            show={showPrintModal}
            onClose={handlePrintModalClose}
            onSubmit={handlePrintModalSubmit}
            poNumber={printModalPart?.poNumber || ''}
            partNumber={printModalPart?.partNumber || printModalPart?.itemName || ''}
            quantity={printModalPart?.receivedInThisShipment || ''}
            receivingShipmentId={createdShipmentId ? createdShipmentId.toString() : ''}
            poReceivingId={printModalPart?.poReceivingID?.toString() || ''}
            partId={printModalPart?.partId ?? printModalPart?.itemID ?? null}
            poItemDetailId={printModalPart?.poItemDetailID?.toString() || ''}
            supplierName={resolveSupplierName(printModalPart)}
            userId={localStorage.getItem('UserId') || ''}
            initialPrinter=""
            initialNumberOfLabels=""
            initialLotNumbers={[{ lotNumber: '', quantity: 1 }]}
          />

          <DocumentViewerModal
            show={showDocumentViewer}
            onHide={handleCloseDocumentViewer}
            documentUrl={documentViewerUrl}
            documentName={documentViewerName}
            onDownload={handleDownloadCurrentDocument}
          />
        </>
      )}
    </div>
  );
});

export default MappingsContent;
